import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Mail, MapPin, Phone, Linkedin, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { IyafWordmark } from "@/components/brand/IyafMark";
import { org } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Welcome.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    }
    setLoading(false);
  };

  return (
    <footer className="relative bg-iyaf-ink text-white mt-24 overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full border border-white/10 animate-spin-slow" />
      <div className="pointer-events-none absolute top-20 -right-10 w-[260px] h-[260px] rounded-full border border-white/5" />
      <div className="container-iyaf py-20 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="mb-6">
              <IyafWordmark variant="main" size="lg" inverse />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight max-w-md mb-2 text-balance">{t("footer.tagline")}</h3>
            <p className="text-white/55 text-sm mb-6 max-w-md">{t("footer.newsletterDesc")}</p>
            <form onSubmit={subscribe} className="flex max-w-md" data-testid="footer-newsletter-form">
              <input data-testid="footer-newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("footer.newsletterPlaceholder")} className="flex-1 bg-white/10 rounded-s-full px-5 py-3 placeholder:text-white/40 outline-none border border-white/10 focus:border-white/30" />
              <button data-testid="footer-newsletter-submit" disabled={loading} className="px-5 rounded-e-full bg-iyaf-blue hover:bg-white hover:text-iyaf-blue transition-colors font-semibold">
                {loading ? "…" : <ArrowUpRight size={18} />}
              </button>
            </form>
          </div>
          <div className="lg:col-span-2">
            <p className="eyebrow text-white/40 mb-4">{t("footer.exploreEyebrow")}</p>
            <ul className="space-y-2.5 text-white/80">
              <li><Link className="hover:text-white" to="/about">{t("nav.about")}</Link></li>
              <li><Link className="hover:text-white" to="/model">{t("nav.model")}</Link></li>
              <li><Link className="hover:text-white" to="/impact">{t("nav.impact")}</Link></li>
              <li><Link className="hover:text-white" to="/stories">{t("nav.stories")}</Link></li>
              <li><Link className="hover:text-white" to="/engage">{t("nav.engage")}</Link></li>
              <li><Link className="hover:text-white" to="/contact">{t("contact.eyebrow")}</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <p className="eyebrow text-white/40 mb-4">{t("footer.armsEyebrow")}</p>
            <ul className="space-y-2.5 text-white/80">
              <li><Link className="hover:text-white" to="/iyaf-hub">{t("arms.hub.title")}</Link></li>
              <li><Link className="hover:text-white" to="/iyaf-social">{t("arms.social.title")}</Link></li>
              <li><Link className="hover:text-white" to="/iyaf-consult">{t("arms.consult.title")}</Link></li>
              <li><Link className="hover:text-white" to="/iyaf-venture">{t("arms.venture.title")}</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="eyebrow text-white/40 mb-4">{t("footer.contactEyebrow")}</p>
            <ul className="space-y-3 text-white/80 text-sm">
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0" /> {t("footerExtras.address")}</li>
              <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 shrink-0" /> {org.email}</li>
              <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5 shrink-0" /> {org.phone}</li>
            </ul>
            <div className="flex gap-2 mt-5">
              <a href={org.socials.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-iyaf-ink transition" aria-label="LinkedIn"><Linkedin size={15} /></a>
              <a href={org.socials.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-iyaf-ink transition" aria-label="Instagram"><Instagram size={15} /></a>
              <a href={org.socials.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-iyaf-ink transition" aria-label="Facebook"><Facebook size={15} /></a>
            </div>
          </div>
        </div>
        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} {t("footerExtras.orgFullName")}. {t("footer.rights")}</p>
          <p>{t("footerExtras.signoff")}</p>
        </div>
      </div>
    </footer>
  );
}
